<template>
  <div class="dialog-backdrop" @click.self="close">
    <div class="dialog-paper">
      <div class="dialog-header">
        <h2>팀 목표</h2>
        <button class="close-btn" @click="close"><i class="ph ph-x"></i></button>
      </div>

      <div class="dialog-body">
        <!-- 목표 추가 -->
        <div class="goal-add-section">
          <div class="goal-add-row">
            <input 
              v-model="newGoal.title" 
              placeholder="목표를 입력하세요" 
              class="goal-input"
              @keyup.enter="addGoal"
            />
            <input 
              v-model="newGoal.target_date" 
              type="date" 
              class="goal-date"
            />
            <button class="goal-add-btn" @click="addGoal">
              <i class="ph ph-plus"></i>
            </button>
          </div>
        </div>

        <!-- 진행 중 목표 -->
        <div class="goal-section" v-if="activeGoals.length">
          <div class="section-label">진행 중</div>
          <ul class="goal-list">
            <li v-for="g in activeGoals" :key="g.idx" class="goal-item">
              <button class="goal-check" @click="toggleGoal(g)">
                <i class="ph ph-circle"></i>
              </button>
              <div class="goal-content">
                <span class="goal-title">{{ g.title }}</span>
                <div class="goal-meta">
                  <span class="goal-author">{{ g.creator_name || g.created_by }}</span>
                  <span v-if="g.target_date" class="goal-date-tag">
                    <i class="ph ph-calendar-blank"></i>
                    {{ formatDate(g.target_date) }}
                  </span>
                </div>
              </div>
              <button v-if="g.created_by === mbId" class="goal-delete" @click="deleteGoal(g)">
                <i class="ph ph-trash"></i>
              </button>
            </li>
          </ul>
        </div>

        <!-- 완료된 목표 -->
        <div class="goal-section" v-if="completedGoals.length">
          <div class="section-label completed-label" @click="showCompleted = !showCompleted">
            완료 ({{ completedGoals.length }})
            <i class="ph" :class="showCompleted ? 'ph-caret-up' : 'ph-caret-down'"></i>
          </div>
          <ul class="goal-list" v-if="showCompleted">
            <li v-for="g in completedGoals" :key="g.idx" class="goal-item done">
              <button class="goal-check checked" @click="toggleGoal(g)">
                <i class="ph-fill ph-check-circle"></i>
              </button>
              <div class="goal-content">
                <span class="goal-title">{{ g.title }}</span>
                <div class="goal-meta">
                  <span class="goal-author">{{ g.creator_name || g.created_by }}</span>
                </div>
              </div>
              <button v-if="g.created_by === mbId" class="goal-delete" @click="deleteGoal(g)">
                <i class="ph ph-trash"></i>
              </button>
            </li>
          </ul>
        </div>

        <div v-if="!activeGoals.length && !completedGoals.length" class="empty-state">
          <i class="ph ph-target"></i>
          <p>아직 팀 목표가 없습니다</p>
          <span>위에서 첫 번째 목표를 추가해보세요</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted } from 'vue';

export default {
  props: {
    oth_close: { type: Function, required: true }
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const getMbid = inject('getMbid');
    const mbId = getMbid();

    const goals = ref([]);
    const showCompleted = ref(false);
    const newGoal = ref({ title: '', target_date: '' });

    const mainGroupIdx = ref(null);

    const activeGoals = computed(() => goals.value.filter(g => g.status !== 'done'));
    const completedGoals = computed(() => goals.value.filter(g => g.status === 'done'));

    async function getMainGroupIdx() {
      const res = await sql_query(`SELECT main_group_idx FROM group_member WHERE mb_id='${mbId}' AND status='join' LIMIT 1`);
      if (res?.length) mainGroupIdx.value = res[0].main_group_idx;
    }

    async function loadGoals() {
      if (!mainGroupIdx.value) return;
      const res = await sql_query(`
        SELECT g.*, m.name as creator_name 
        FROM group_goal g 
        LEFT JOIN member m ON g.created_by = m.mb_id
        WHERE g.main_group_idx = ${mainGroupIdx.value}
        ORDER BY g.status ASC, g.created_at DESC
      `);
      if (Array.isArray(res)) goals.value = res;
    }

    async function addGoal() {
      const title = newGoal.value.title.trim();
      if (!title || !mainGroupIdx.value) return;
      const esc = (s) => s.replace(/'/g, "\\'");
      const datePart = newGoal.value.target_date ? `'${newGoal.value.target_date}'` : 'NULL';
      await sql_query(`
        INSERT INTO group_goal (main_group_idx, title, target_date, status, created_by, created_at)
        VALUES (${mainGroupIdx.value}, '${esc(title)}', ${datePart}, 'active', '${mbId}', NOW())
      `);
      newGoal.value = { title: '', target_date: '' };
      await loadGoals();
    }

    async function toggleGoal(g) {
      const newStatus = g.status === 'done' ? 'active' : 'done';
      await sql_query(`UPDATE group_goal SET status='${newStatus}' WHERE idx=${g.idx}`);
      await loadGoals();
    }

    async function deleteGoal(g) {
      if (!confirm('이 목표를 삭제하시겠습니까?')) return;
      await sql_query(`DELETE FROM group_goal WHERE idx=${g.idx}`);
      await loadGoals();
    }

    function formatDate(d) {
      if (!d) return '';
      const date = new Date(d);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }

    function close() {
      props.oth_close(false);
    }

    onMounted(async () => {
      await getMainGroupIdx();
      await loadGoals();
    });

    return {
      goals, newGoal, activeGoals, completedGoals, showCompleted,
      mbId, addGoal, toggleGoal, deleteGoal, formatDate, close
    };
  }
};
</script>

<style scoped>
.dialog-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-top, 9999);
}

.dialog-paper {
  width: 420px;
  max-width: 95vw;
  max-height: 80vh;
  background: rgba(20, 20, 35, 0.95);
  backdrop-filter: blur(24px);
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 24px 80px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.dialog-header h2 {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  transition: color 0.15s;
}
.close-btn:hover { color: #fff; }

.dialog-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.goal-add-section {
  margin-bottom: 20px;
}

.goal-add-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.goal-input {
  flex: 1;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}
.goal-input:focus {
  border-color: rgba(99, 102, 241, 0.6);
}
.goal-input::placeholder { color: rgba(255,255,255,0.3); }

.goal-date {
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.7);
  font-size: 13px;
  outline: none;
  width: 130px;
}

.goal-add-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: rgba(99, 102, 241, 0.8);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  flex-shrink: 0;
}
.goal-add-btn:hover { background: rgba(99, 102, 241, 1); }

.section-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
}

.completed-label {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  user-select: none;
}
.completed-label:hover { color: rgba(255,255,255,0.6); }

.goal-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.goal-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  transition: background 0.15s;
}
.goal-item:hover { background: rgba(255,255,255,0.05); }

.goal-item.done { opacity: 0.5; }

.goal-check {
  background: none;
  border: none;
  color: rgba(255,255,255,0.3);
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  display: flex;
  flex-shrink: 0;
  transition: color 0.15s;
}
.goal-check:hover { color: rgba(99, 102, 241, 0.8); }
.goal-check.checked { color: rgba(99, 102, 241, 0.9); }

.goal-content {
  flex: 1;
  min-width: 0;
}

.goal-title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255,255,255,0.9);
  display: block;
}

.goal-item.done .goal-title {
  text-decoration: line-through;
  color: rgba(255,255,255,0.5);
}

.goal-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
}

.goal-author {
  font-size: 11px;
  color: rgba(255,255,255,0.35);
}

.goal-date-tag {
  font-size: 11px;
  color: rgba(99, 102, 241, 0.7);
  display: flex;
  align-items: center;
  gap: 3px;
}

.goal-delete {
  background: none;
  border: none;
  color: rgba(255,255,255,0.15);
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  opacity: 0;
  transition: all 0.15s;
}
.goal-item:hover .goal-delete { opacity: 1; }
.goal-delete:hover { color: #f87171; }

.goal-section {
  margin-bottom: 20px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: rgba(255,255,255,0.3);
}
.empty-state > i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.4;
}
.empty-state > p {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
}
.empty-state > span {
  font-size: 12px;
}
</style>
